import { EMPTY_FILTERS, normaliseLanguage, referenceSale, selectSlabs } from './slabModel.mjs';

const text = value => String(value ?? '').trim();
const keyPart = value => text(value).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ');

export function tradeAmount(value) {
  const input = text(value).replace(/[£\s]/g, '');
  if (input.includes(',') && !/^\d{1,3}(?:,\d{3})+(?:\.\d{1,2})?k?$/i.test(input)) return null;
  const match = input.replace(/,/g, '').match(/^(\d+(?:\.\d{1,2})?)(k)?$/i);
  if (!match) return null;
  const cents = Math.round(Number(match[1]) * (match[2] ? 100000 : 100));
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null;
}

export function tradePrintingKey(slab) {
  return [slab.grader, slab.grade, slab.name, slab.set, slab.number, slab.language, slab.variant, slab.edition, slab.additionalInformation].map(keyPart).join('|');
}

export function selectTradeSlabs(slabs, query) {
  let value = text(query).replace(/\bsirs\b/gi, 'sir').replace(/\bsars\b/gi, 'sar');
  const grades = [], languages = [], graders = [];
  value = value.replace(/\b(ACE|PSA|CGC|BGS|SGC|TAG)\s*(\d{1,2}(?:\.\d)?)\b/gi, (_, grader, grade) => {
    grades.push(`${grader.toUpperCase()} ${Number(grade)}`); return '';
  });
  value = value.replace(/\b(English|Japanese|Korean|Chinese|German|French|Italian|Spanish|Portuguese|Dutch|JPN|JP|EN)\b/gi, language => {
    languages.push(normaliseLanguage(language)); return '';
  });
  value = value.replace(/\b(ACE|PSA|CGC|BGS|SGC|TAG)\b/gi, grader => { graders.push(grader.toUpperCase()); return ''; });
  const specialArts = /\bspecial\s+arts?\b/i.test(value) || /\bsir\s*[/|&]\s*sar\b/i.test(value);
  if (specialArts) value = value.replace(/\bspecial\s+arts?\b|\bsir\b|\bsar\b/gi, '').replace(/[/|&]/g, ' ');
  return selectSlabs(slabs, { query: value, filters: { ...EMPTY_FILTERS, grades, languages, graders, ...(specialArts ? { rarities: ['Special Illustration Rare', 'Special Art Rare'] } : {}) } });
}

export function tradePrice(slab, agreed = {}) {
  if (text(agreed[slab.cert])) {
    const cents = tradeAmount(agreed[slab.cert]);
    return cents ? { cents, high: cents, basis: 'Agreed value', date: null } : null;
  }
  if (slab.grader !== 'ACE') return null;
  const sale = referenceSale(slab, 'ACE');
  if (!sale?.date || !Number.isFinite(Date.parse(sale.date))) return null;
  const low = sale.price ?? sale.low, high = sale.price ?? sale.high;
  if (!(low > 0 && high >= low)) return null;
  return { cents: Math.round(low * 100), high: Math.round(high * 100), basis: sale.status === 'range' ? 'ACE range · lower end' : 'ACE last sold', date: sale.date };
}

export function tradePool(slabs, { agreed = {}, includePc = false, includeInvestment = false, favourites = '', excludeLabels = [] } = {}, universe = slabs) {
  const groups = new Map();
  for (const slab of universe) {
    const key = tradePrintingKey(slab);
    if (!groups.has(key)) groups.set(key, new Set());
    groups.get(key).add(slab.cert);
  }
  const names = text(favourites).split(/[,;\n]+/).map(keyPart).filter(Boolean);
  const seen = new Set(), eligible = [], unpriced = [], blocked = [];
  for (const slab of slabs) {
    if (seen.has(slab.cert)) continue;
    seen.add(slab.cert);
    let reason = null;
    if (slab.source !== 'business') reason = 'Personal vault';
    else if (!/^\d{4,12}$/.test(slab.cert)) reason = 'Certificate unavailable';
    else if (slab.physicalIssue) reason = 'Physical problem needs owner review';
    else if (slab.stockStatus !== 'in_stock') reason = slab.stockStatus ? 'Already committed or out of stock' : 'Availability not recorded';
    else if (slab.handoffState && slab.handoffState !== 'none') reason = 'Handover in progress';
    else if (slab.intendedUse === 'randys_pc' && !includePc) reason = 'Randy’s PC protected';
    else if (slab.intendedUse === 'investment' && !includeInvestment) reason = 'Investment protected';
    else if (excludeLabels.some(label => slab.tags.some(tag => keyPart(tag) === keyPart(label)))) reason = 'Excluded label';
    if (reason) { blocked.push({ slab, reason }); continue; }
    const price = tradePrice(slab, agreed);
    const entry = { slab, price, copiesOwned: groups.get(tradePrintingKey(slab))?.size || 1, favourite: names.some(name => keyPart(`${slab.name} ${slab.set}`).includes(name)), spare: false };
    (price ? eligible : unpriced).push(entry);
  }
  // Mark only copies beyond one owned copy as spares, independently of price.
  const candidateGroups = new Map();
  for (const entry of [...eligible, ...unpriced]) {
    const key = tradePrintingKey(entry.slab);
    if (!candidateGroups.has(key)) candidateGroups.set(key, []);
    candidateGroups.get(key).push(entry);
  }
  for (const entries of candidateGroups.values()) {
    entries.sort((a, b) => Number(Boolean(b.price)) - Number(Boolean(a.price)) || a.slab.cert.localeCompare(b.slab.cert));
    const spares = Math.max(0, entries[0].copiesOwned - 1);
    entries.forEach((entry, index) => { entry.spare = index < spares; });
  }
  return { eligible, unpriced, blocked };
}

// Bounded, deterministic packing. Prefer spare copies, then requested Pokemon;
// improve the value fit without trading those preferences away. This is a
// proposal, not a claim of a mathematically optimal subset or an agreed deal.
export function buildTrade(slabs, { target, favourDuplicates = true, ...options }, universe = slabs) {
  const targetCents = tradeAmount(target);
  if (!targetCents) throw new Error('Enter a positive target such as 8000 or 8k.');
  const pool = tradePool(slabs, options, universe);
  const priority = entry => (favourDuplicates && entry.spare ? 100 : 0) + (entry.favourite ? 10 : 0);
  const ranked = [...pool.eligible].sort((a, b) => priority(b) - priority(a) || b.price.cents - a.price.cents || a.slab.cert.localeCompare(b.slab.cert));
  const selected = new Map();
  let total = 0;
  for (const entry of ranked) {
    if (entry.price.cents <= targetCents - total) { selected.set(entry.slab.cert, entry); total += entry.price.cents; }
  }
  for (let pass = 0; pass < 2 && total < targetCents; pass++) {
    const remaining = ranked.filter(entry => !selected.has(entry.slab.cert)).sort((a, b) => a.price.cents - b.price.cents || a.slab.cert.localeCompare(b.slab.cert));
    let best = null;
    // At most 64 replacement scans keep a multi-thousand-card proposal quick.
    for (const old of [...selected.values()].slice(0, 64)) {
      const allowance = targetCents - total + old.price.cents;
      let left = 0, right = remaining.length - 1;
      while (left <= right) {
        const a = remaining[left], b = left === right ? null : remaining[right];
        const amount = a.price.cents + (b?.price.cents || 0);
        if (amount > allowance) { right--; continue; }
        const gain = amount - old.price.cents;
        if (gain > 0 && priority(a) + (b ? priority(b) : 0) >= priority(old) && (!best || gain > best.gain)) best = { old, a, b, gain };
        left++;
      }
    }
    if (!best) break;
    selected.delete(best.old.slab.cert); selected.set(best.a.slab.cert, best.a);
    if (best.b) selected.set(best.b.slab.cert, best.b);
    total += best.gain;
  }
  return { ...pool, selected: [...selected.values()], targetCents, total, shortfall: targetCents - total };
}

export function tradeTotals(entries) {
  return entries.reduce((total, entry) => ({
    count: total.count + 1, cents: total.cents + entry.price.cents, high: total.high + entry.price.high,
    spares: total.spares + Number(entry.spare), agreed: total.agreed + Number(entry.price.basis === 'Agreed value'),
  }), { count: 0, cents: 0, high: 0, spares: 0, agreed: 0 });
}

export function tradePickLinks(entries, reference = '') {
  return Array.from({ length: Math.ceil(entries.length / 200) }, (_, index) => {
    const batch = entries.slice(index * 200, (index + 1) * 200), total = tradeTotals(batch);
    const params = new URLSearchParams({ reason: 'trade', cert: batch.map(entry => entry.slab.cert).join(','), reference: text(reference).slice(0, 200), note: `Trade proposal: £${(total.cents / 100).toFixed(2)}. ACE sale references and any entered agreed values; review before reserving.` });
    return { count: batch.length, url: `/slab-inventory/picks?${params}` };
  });
}

export function tradeCsv(entries) {
  const field = value => '"' + (/^[=+@-]/.test(text(value)) ? "'" : '') + text(value).replace(/"/g, '""') + '"';
  return [['Cert', 'Card', 'Set', 'Number', 'Grade', 'Draft trade GBP', 'Basis', 'Sale date', 'ACE low GBP', 'ACE high GBP', 'PSA low GBP', 'PSA high GBP', 'Location'].join(','), ...entries.map(({ slab, price }) => {
    const ace = referenceSale(slab, 'ACE'), psa = referenceSale(slab, 'PSA');
    return [slab.cert, slab.name, slab.set, slab.number, slab.gradeKey, (price.cents / 100).toFixed(2), price.basis, price.date || '', ace?.price ?? ace?.low ?? '', ace?.price ?? ace?.high ?? '', psa?.price ?? psa?.low ?? '', psa?.price ?? psa?.high ?? '', [slab.storageLocation.label, slab.storageLocation.detail].join(' · ')].map(field).join(',');
  })].join('\r\n');
}

export function tradeText(entries) {
  const total = tradeTotals(entries), gbp = cents => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(cents / 100);
  return [`Trade proposal · ${entries.length} slabs · ${gbp(total.cents)}`, 'Draft values, subject to agreement.', '', ...entries.map(({ slab, price }) => `${slab.name} · ${slab.set} #${slab.number} · ${slab.gradeKey} · cert ${slab.cert} · ${gbp(price.cents)}`)].join('\n');
}
